import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { styles } from './SignupPrompt.css';
import { JBButton } from '@jbhi-fi/lanyard-ui';
import JBCheckBox from '@jbhi-fi/lanyard-ui/components/Checkbox';
import { SocialLogins } from '../../../../loginCreate/socialLogins/SocialLogins';
import { EmailInput } from './emailInput/EmailInput';
import { handleLoginCreate } from '../../../../../../utils/signInOutUp/AuthenticationHelper';
import { FormBreak } from '../../../../common/formBreak/FormBreak';
import { isEmailValid } from '../../../../../../utils/common/validateEmail/validateEmail';
import { trackingContext, formAction, useTracking } from '@jbhi-fi/components/src/Tracking';

const EMAIL_ERROR_MESSAGE = "Your email address doesn't look right. Please try again.";
const CHECKBOX_ERROR_MESSAGE = 'Please accept the terms and conditions to continue.';

export type SignupPromptPropsType = {
  heading: string;
  disclaimer: string | ReactNode;
  returnUrl?: string;
  customerEmail?: string;
  isInline?: boolean;
  gtmContext?: trackingContext;
};

export const SignupPrompt = ({
  heading,
  disclaimer,
  customerEmail = '',
  returnUrl = '/account/login?pageTheme=perks',
  isInline = false,
  gtmContext = trackingContext.account,
}: SignupPromptPropsType): JSX.Element => {
  const [email, setEmail] = useState(customerEmail);
  const [emailHasError, setEmailHasError] = useState(false);
  const [termsPolicyChecked, setTermsPolicyChecked] = useState(false);
  const [termsPolicyErrorDisplayed, setTermsPolicyErrorDisplayed] = useState<string | undefined>();
  const fromReceiptsMicrosite = gtmContext === trackingContext.receipts;
  const { tracker } = useTracking({
    context: gtmContext,
    category: fromReceiptsMicrosite ? 'eReceipts - JB Perks' : 'my account - JB Perks',
    group: 'landing',
  });
  const errorRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    tracker.trackFormEvent({ action: formAction.stepView });
  }, []);

  const handleSocialRegistration = (domainHint: string) => {
    handleLoginCreate(
      returnUrl,
      {
        domainHint,
        userJourneyMode: 'EXTERNAL_ACCOUNT',
        pageTheme: 'perks',
      },
      { fromReceiptsMicrosite }
    );
  };

  const handleEmailRegistration = (): void => {
    handleLoginCreate(
      returnUrl,
      {
        loginHint: email,
        pageTheme: 'perks',
      },
      { fromReceiptsMicrosite }
    );
  };

  const handleClick = (event: React.FormEvent, domainHint?: string): void => {
    event.preventDefault();
    if ((!domainHint && !isEmailValid(email)) || !termsPolicyChecked) {
      if (!domainHint && !isEmailValid(email)) {
        setEmailHasError(true);
        tracker.trackError({ errorMsg: EMAIL_ERROR_MESSAGE, contextOverride: trackingContext.form });
      }
      if (!termsPolicyChecked) {
        tracker.trackError({ errorMsg: CHECKBOX_ERROR_MESSAGE, contextOverride: trackingContext.form });
        setTermsPolicyErrorDisplayed(CHECKBOX_ERROR_MESSAGE);
        errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
      return;
    }
    tracker.trackClick({ label: domainHint?.split('.')?.[0] ?? 'Continue' });
    domainHint ? handleSocialRegistration(domainHint) : handleEmailRegistration();
  };

  useEffect(() => {
    if (emailHasError) setEmailHasError(false);
  }, [emailHasError]);

  return (
    <form onSubmit={handleClick} className={styles.container({ isInline })}>
      {heading ? <h2 className={styles.heading}>{heading}</h2> : null}
      <div className={styles.loginButtons}>
        <SocialLogins handleClick={handleClick} heading="Create a JB Perks membership with:" />
      </div>
      <FormBreak margin={'20px 0'} />
      <EmailInput
        errorMsg={EMAIL_ERROR_MESSAGE}
        onChange={event => setEmail(event.target.value ?? '')}
        value={email}
        hasError={emailHasError}
      />
      <JBCheckBox
        className={styles.checkbox}
        id="accept-terms"
        name="accept-terms"
        value="accepted"
        hasError={!!termsPolicyErrorDisplayed}
        checked={termsPolicyChecked}
        onChange={() => {
          setTermsPolicyChecked(!termsPolicyChecked);
          setTermsPolicyErrorDisplayed(undefined);
        }}
        label={disclaimer}
      />
      <span ref={errorRef} />
      {termsPolicyErrorDisplayed ? <p className={styles.policyError}>{termsPolicyErrorDisplayed}</p> : null}
      <JBButton type="primary" fullWidth submit onClick={() => null} classes={styles.button}>
        Continue
      </JBButton>
    </form>
  );
};
